import React, { useId } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";

/** The colour of a figure and its icon. */
const TONES = {
  primary: "text-primary-1",
  info: "text-sky-600",
  warning: "text-amber-600",
  success: "text-green-600",
  danger: "text-red-500",
} as const;

export type Tone = keyof typeof TONES;

/** A headline figure; assistive tech reads it as a group named by its label. */
const StatCard: React.FunctionComponent<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: React.ElementType;
  tone?: Tone;
  className?: string;
}> = ({ label, value, sub, icon: Icon, tone = "primary", className }) => {
  const labelId = useId();

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className={twMerge(
        "flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-white px-5 py-4",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          id={labelId}
          className="text-xs font-bold uppercase tracking-widest text-gray-500"
        >
          {label}
        </span>
        {Icon ? (
          <Icon
            className={classNames("h-4 w-4 shrink-0 opacity-70", TONES[tone])}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <div
        className={classNames("text-2xl font-bold leading-tight", TONES[tone])}
      >
        {value}
      </div>
      {sub ? <div className="text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
};

export default StatCard;
